import React from 'react';
import { Play, Lock, Square, RectangleHorizontal, RectangleVertical } from 'lucide-react';
import { CAPTION_PRESETS } from '../lib/CaptionPresets';
import { CaptionPhonePreview } from './CaptionPhonePreview';
import './ImportConfigPanel.css';

const ASPECT_RATIOS = [
    { id: '9:16', label: '9:16', icon: RectangleVertical, enabled: true, description: 'Vertical (Shorts/Reels)' },
    { id: '16:9', label: '16:9', icon: RectangleHorizontal, enabled: false, description: 'Horizontal (YouTube)' },
    { id: '1:1', label: '1:1', icon: Square, enabled: false, description: 'Square (Instagram)' },
];

export const ImportConfigPanel = ({
    videoMeta,
    captionConfig,
    onConfigChange,
    aspectRatio,
    onAspectChange,
    onProcess,
    onCancel,
    isProcessing = false
}) => {
    const { title, thumbnail, duration } = videoMeta || {};

    const handleStyleSelect = (styleId) => {
        onConfigChange({ ...captionConfig, styleId });
    };

    const handleSizeChange = (e) => {
        onConfigChange({ ...captionConfig, size: parseInt(e.target.value, 10) });
    };

    const handlePositionChange = (e) => {
        onConfigChange({ ...captionConfig, verticalPosition: parseInt(e.target.value, 10) });
    };

    const handleSaveDefaultChange = (e) => {
        onConfigChange({ ...captionConfig, saveAsDefault: e.target.checked });
    };

    const renderPreviewText = (preset) => {
        const style = {
            fontFamily: preset.preview.fontFamily,
            fontWeight: preset.preview.fontWeight || 'normal',
            textTransform: preset.preview.textTransform || 'none',
            color: preset.preview.color,
            textShadow: preset.preview.textShadow || 'none',
            fontSize: '11px',
        };

        if (preset.id === 'deep_diver') {
            return (
                <span style={{ ...style, background: '#fff', color: '#000', padding: '2px 4px', borderRadius: '2px' }}>
                    Sample
                </span>
            );
        }

        const hlColor = preset.preview.highlight || preset.preview.color;
        return (
            <span style={style}>
                Get <span style={{ color: hlColor }}>Started</span>
            </span>
        );
    };

    return (
        <div className="import-config-panel">
            {/* Video Preview Header */}
            <div className="import-video-header">
                <div className="import-thumbnail-container">
                    {thumbnail ? (
                        <img src={thumbnail} alt={title} className="import-thumbnail" />
                    ) : (
                        <div className="import-thumbnail-placeholder" />
                    )}
                </div>
                <div className="import-video-info">
                    <h2 className="import-video-title">{title || 'Video'}</h2>
                    {duration && (
                        <span className="import-video-duration">
                            {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}
                        </span>
                    )}
                </div>
            </div>

            {/* Main Config Area */}
            <div className="import-config-content">
                {/* Left: Caption Settings */}
                <div className="import-config-left">
                    <h3 className="config-section-title">Caption Style</h3>

                    <div className="caption-styles-grid-compact">
                        {CAPTION_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                className={`style-option ${captionConfig.styleId === preset.id ? 'active' : ''}`}
                                onClick={() => handleStyleSelect(preset.id)}
                            >
                                <div className="style-preview-mini" style={{ background: preset.preview.bg === '#fff' ? '#555' : '#222' }}>
                                    {renderPreviewText(preset)}
                                </div>
                                <span className="style-name">{preset.name}</span>
                            </button>
                        ))}
                    </div>

                    <div className="config-sliders">
                        <div className="slider-group">
                            <label>
                                <span>Size</span>
                                <span className="slider-value">{captionConfig.size}</span>
                            </label>
                            <input
                                type="range"
                                min="40"
                                max="150"
                                value={captionConfig.size}
                                onChange={handleSizeChange}
                                className="config-slider"
                            />
                        </div>

                        <div className="slider-group">
                            <label>
                                <span>Vertical Position</span>
                                <span className="slider-value">{captionConfig.verticalPosition}%</span>
                            </label>
                            <input
                                type="range"
                                min="10"
                                max="90"
                                value={captionConfig.verticalPosition}
                                onChange={handlePositionChange}
                                className="config-slider"
                            />
                        </div>
                    </div>

                    <label className="save-default-checkbox">
                        <input
                            type="checkbox"
                            checked={captionConfig.saveAsDefault}
                            onChange={handleSaveDefaultChange}
                        />
                        <span>Save as default</span>
                    </label>
                </div>

                {/* Right: Phone Preview */}
                <div className="import-config-right">
                    <CaptionPhonePreview
                        styleId={captionConfig.styleId}
                        size={captionConfig.size}
                        verticalPosition={captionConfig.verticalPosition}
                    />
                </div>
            </div>

            {/* Aspect Ratio Selector */}
            <div className="aspect-ratio-section">
                <h3 className="config-section-title">Aspect Ratio</h3>
                <div className="aspect-ratio-options">
                    {ASPECT_RATIOS.map((ratio) => (
                        <button
                            key={ratio.id}
                            className={`aspect-option ${aspectRatio === ratio.id ? 'active' : ''} ${!ratio.enabled ? 'disabled' : ''}`}
                            onClick={() => ratio.enabled && onAspectChange(ratio.id)}
                            disabled={!ratio.enabled}
                            title={ratio.description}
                        >
                            <ratio.icon size={20} />
                            <span className="aspect-label">{ratio.label}</span>
                            {!ratio.enabled && (
                                <>
                                    <Lock size={12} className="lock-icon" />
                                    <span className="coming-soon-badge">Coming Soon</span>
                                </>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="import-actions">
                <button className="import-btn-secondary" onClick={onCancel} disabled={isProcessing}>
                    Cancel
                </button>
                <button className="import-btn-primary" onClick={onProcess} disabled={isProcessing}>
                    <Play size={18} />
                    <span>{isProcessing ? 'Starting...' : 'Import & Process'}</span>
                </button>
            </div>
        </div>
    );
};
